import { useEffect } from "react";

// "Ink" suave (#262626) — preferido sobre preto puro pra texto escuro porque
// reduz a sensação de "marcação à caneta" em fundos coloridos vivos. Usado
// como fallback de foreground escuro em todo o produto.
const INK_HSL = "20 20% 15%";
const WHITE_HSL = "0 0% 100%";
const INK_HEX = "#262626";
const WHITE_HEX = "#FFFFFF";

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  return {
    r: parseInt(result[1]!, 16),
    g: parseInt(result[2]!, 16),
    b: parseInt(result[3]!, 16),
  };
}

function hexToHsl(hex: string): string | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Luminância relativa WCAG 2.1 (com gamma correction). Substitui a fórmula
// ingênua (0.299R + 0.587G + 0.114B) anterior, que é "luminância percebida"
// pra image processing — não pra cálculo de contraste WCAG. A diferença
// muda a decisão de foreground em uma faixa de cores médias.
function relativeLuminance(hex: string): number | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const chan = (c: number): number => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * chan(rgb.r) + 0.7152 * chan(rgb.g) + 0.0722 * chan(rgb.b);
}

function contrastRatio(hex1: string, hex2: string): number | null {
  const la = relativeLuminance(hex1);
  const lb = relativeLuminance(hex2);
  if (la == null || lb == null) return null;
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Escolhe foreground (ink vs branco) por contraste WCAG real e sinaliza
 * se a melhor escolha disponível ainda fica abaixo do mínimo AA (4.5:1).
 *
 * `warn: true` significa: nenhum dos dois foregrounds atinge 4.5:1 com a
 * cor de background — o lojista escolheu uma cor problemática. UI deve
 * mostrar selo de aviso, mas NÃO bloquear (lojista é dono da escolha).
 */
export function bestForeground(hex: string): {
  fgHsl: string;
  fgHex: string;
  contrast: number;
  warn: boolean;
} {
  const cInk = contrastRatio(hex, INK_HEX) ?? 1;
  const cWhite = contrastRatio(hex, WHITE_HEX) ?? 1;
  const useInk = cInk >= cWhite;
  const best = useInk ? cInk : cWhite;
  return {
    fgHsl: useInk ? INK_HSL : WHITE_HSL,
    fgHex: useInk ? INK_HEX : WHITE_HEX,
    contrast: best,
    warn: best < 4.5,
  };
}

export function useStoreColors(colors: {
  colorPrimary?: string | null;
  colorSecondary?: string | null;
  colorAccent?: string | null;
} | null | undefined) {
  useEffect(() => {
    const root = document.documentElement;

    if (colors?.colorPrimary) {
      const hsl = hexToHsl(colors.colorPrimary);
      if (hsl) {
        root.style.setProperty("--primary", hsl);
        root.style.setProperty("--ring", hsl);
        root.style.setProperty("--primary-foreground", bestForeground(colors.colorPrimary).fgHsl);
      }
    }

    if (colors?.colorSecondary) {
      const hsl = hexToHsl(colors.colorSecondary);
      if (hsl) {
        root.style.setProperty("--secondary", hsl);
        root.style.setProperty("--secondary-foreground", bestForeground(colors.colorSecondary).fgHsl);
      }
    }

    if (colors?.colorAccent) {
      const hsl = hexToHsl(colors.colorAccent);
      if (hsl) {
        root.style.setProperty("--accent", hsl);
        root.style.setProperty("--accent-foreground", bestForeground(colors.colorAccent).fgHsl);
      }
    }

    return () => {
      root.style.removeProperty("--primary");
      root.style.removeProperty("--ring");
      root.style.removeProperty("--primary-foreground");
      root.style.removeProperty("--secondary");
      root.style.removeProperty("--secondary-foreground");
      root.style.removeProperty("--accent");
      root.style.removeProperty("--accent-foreground");
    };
  }, [colors?.colorPrimary, colors?.colorSecondary, colors?.colorAccent]);
}
